import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import FullScreenLoadingSpinner from '../shared/FullScreenLoading';
import {
  Box,
  Button,
  Grid,
  Modal,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowModel,
} from '@mui/x-data-grid';
import CircleIcon from '@mui/icons-material/Circle';
import styles from '../../styles';
import {useNavigate, useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {getDrillAsync, getDrillsAsync} from '../../store/drillSlice';
import {PositionGroup} from '../../enums';
import {formatNum, formatTime} from '../../services/formatService';
import {getSpeedMultiplier} from '../../services/measurementService';
import {sanitizeFilename} from '../../helpers';
import {DateTime} from 'luxon';

interface Props {}

interface DrillRow {
  id: number;
  name: string;
  drillLoadScore: number | null;
  description: string | null | undefined;
  count: number | null;
  drillRunCountAvg: number | null;
  durationAvg: number | null;
  strintTotalTimeAvg: number | null;
  playerLoadAvg: number | null;
  playerLoadPerMinAvg: number | null;
  speedAvgAvg: number | null;
}

const Drills: React.FC<Props> = () => {
  const {t} = useTranslation();
  const {teamId} = useParams();
  const navigate = useNavigate();
  const preferredUnits = useAppSelector(
    state => state.profile.profile?.preferredUnits,
  );
  const drillStatus = useAppSelector(state => state.drill.status);
  const drills = useAppSelector(state => state.drill.drills);
  const teamName =
    (useAppSelector(state => state.team.teams) ?? []).find(
      t => t.id === Number(teamId),
    )?.name ?? '';
  const dispatch = useAppDispatch();
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [moreInfoOpen, setMoreInfoOpen] = useState(false);

  const rows = useMemo<DrillRow[]>(() => {
    if (!drills) return [];
    return drills.map(drill => {
      const match = (drill.drillMetrics ?? []).find(
        m => m.positionGroup === selectedGroup,
      );
      return {
        id: drill.id,
        name: drill.name,
        drillLoadScore: match?.drillLoadScore ?? null,
        description: drill.description,
        count: match?.count ?? null,
        drillRunCountAvg: match?.drillRunCountAvg ?? null,
        durationAvg: match?.durationAvg ?? null,
        strintTotalTimeAvg: match?.strintTotalTimeAvg ?? null,
        playerLoadAvg: match?.playerLoadAvg ?? null,
        playerLoadPerMinAvg: match?.playerLoadPerMinAvg ?? null,
        speedAvgAvg: match?.speedAvgAvg ?? null,
      };
    });
  }, [drills, selectedGroup]);

  const formatCount = useCallback(
    (value: DrillRow['count']) =>
      formatNum({
        value,
        decimals: 0,
      }),
    [],
  );

  const formatPlayerLoadPerMin = useCallback(
    (value: DrillRow['playerLoadPerMinAvg']) =>
      formatNum({
        value,
        multiplier: 0.01,
        decimals: 2,
      }),
    [],
  );

  const formatDuration = useCallback(
    (value: DrillRow['durationAvg']) => formatTime(value),
    [],
  );

  const formatDrillRunCount = useCallback(
    (value: DrillRow['drillRunCountAvg']) =>
      formatNum({
        value,
      }),
    [],
  );

  const formatStrintTotalTime = useCallback(
    (value: DrillRow['strintTotalTimeAvg']) => formatTime(value),
    [],
  );

  const formatSpeed = useCallback(
    (value: DrillRow['speedAvgAvg']) =>
      formatNum({
        value,
        multiplier: getSpeedMultiplier(preferredUnits === 'metric'),
        unit: preferredUnits === 'metric' ? 'kph' : 'mph',
      }),
    [preferredUnits],
  );

  const formatCsvValue = useCallback(
    (field: string, raw: DrillRow[keyof DrillRow]) => {
      switch (field) {
        case 'count':
          return formatCount(raw as DrillRow['count']);
        case 'playerLoadPerMinAvg':
          return formatPlayerLoadPerMin(raw as DrillRow['playerLoadPerMinAvg']);
        case 'durationAvg':
          return formatDuration(raw as DrillRow['durationAvg']);
        case 'drillRunCountAvg':
          return formatDrillRunCount(raw as DrillRow['drillRunCountAvg']);
        case 'strintTotalTimeAvg':
          return formatStrintTotalTime(raw as DrillRow['strintTotalTimeAvg']);
        case 'speedAvgAvg':
          return formatSpeed(raw as DrillRow['speedAvgAvg']);
        default:
          return raw;
      }
    },
    [
      formatCount,
      formatDrillRunCount,
      formatDuration,
      formatPlayerLoadPerMin,
      formatSpeed,
      formatStrintTotalTime,
    ],
  );

  const handleNavigateToDrill = useCallback(
    (drillId: number) => {
      dispatch(getDrillAsync({id: drillId}));
      navigate(`/teams/${teamId}/library/drills/${drillId}`);
    },
    [dispatch, navigate, teamId],
  );

  const columns: GridColDef<DrillRow>[] = [
    {
      field: 'id',
      headerName: 'ID',
      hide: true,
    },
    {
      field: 'name',
      headerName: t('general.name') as string,
      width: 200,
      filterable: true,
      renderCell: (params: GridRenderCellParams) => {
        const score = params.row.drillLoadScore;
        const color =
          score === 1
            ? 'success.main'
            : score === 2
              ? 'warning.main'
              : score === 3
                ? 'error.main'
                : undefined;
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              overflow: 'hidden',
            }}>
            {color && <CircleIcon sx={{fontSize: 12, color, flexShrink: 0}} />}
            <Typography noWrap>{params.value}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'count',
      headerName: t('library.sessionCount') as string,
      width: 100,
      type: 'number',
      valueFormatter: params => formatCount(params.value as DrillRow['count']),
    },
    {
      field: 'playerLoadAvg',
      headerName: t('library.avgPlayerLoad') as string,
      width: 140,
      type: 'number',
      valueFormatter: params =>
        formatPlayerLoadPerMin(params.value as DrillRow['playerLoadAvg']),
    },
    {
      field: 'playerLoadPerMinAvg',
      headerName: t('library.avgPlayerLoadPerMin') as string,
      width: 170,
      type: 'number',
      valueFormatter: params =>
        formatPlayerLoadPerMin(params.value as DrillRow['playerLoadPerMinAvg']),
    },
    {
      field: 'durationAvg',
      headerName: t('library.avgDuration') as string,
      width: 120,
      type: 'number',
      valueFormatter: params =>
        formatDuration(params.value as DrillRow['durationAvg']),
    },
    {
      field: 'drillRunCountAvg',
      headerName: t('library.avgRuns') as string,
      width: 140,
      type: 'number',
      valueFormatter: params =>
        formatDrillRunCount(params.value as DrillRow['drillRunCountAvg']),
    },
    {
      field: 'strintTotalTimeAvg',
      headerName: t('library.avgStrintTotalTime') as string,
      width: 140,
      type: 'number',
      valueFormatter: params =>
        formatStrintTotalTime(params.value as DrillRow['strintTotalTimeAvg']),
    },
    {
      field: 'speedAvgAvg',
      headerName: t('library.avgSpeed') as string,
      width: 110,
      type: 'number',
      valueFormatter: params =>
        formatSpeed(params.value as DrillRow['speedAvgAvg']),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: params => (
        <Button
          size="small"
          onClick={event => {
            event.stopPropagation();
            handleNavigateToDrill(Number(params.row.id));
          }}>
          {t('general.view')}
        </Button>
      ),
    },
  ];

  const handleRowClick = useCallback(
    (row: GridRowModel) => {
      const drillId = row.id;
      if (drillId) {
        handleNavigateToDrill(Number(drillId));
      }
    },
    [handleNavigateToDrill],
  );

  useEffect(() => {
    if (teamId) {
      dispatch(getDrillsAsync({teamId: Number(teamId)}));
    }
  }, [dispatch, teamId]);

  if (drillStatus === 'loading') {
    return (
      <>
        <FullScreenLoadingSpinner />
      </>
    );
  }

  const columnDescriptions = [
    {label: t('library.sessionCount'), desc: t('library.sessionCountDesc')},
    {
      label: t('library.avgPlayerLoadPerMin'),
      desc: t('library.avgPlayerLoadPerMinDesc'),
    },
    {label: t('library.avgDuration'), desc: t('library.avgDurationDesc')},
    {label: t('library.avgRuns'), desc: t('library.avgRunsDesc')},
    {
      label: t('library.avgStrintTotalTime'),
      desc: t('library.avgStrintTotalTimeDesc'),
    },
    {label: t('library.avgSpeed'), desc: t('library.avgSpeedDesc')},
  ];

  return (
    <Box>
      <Grid container alignItems="center" sx={{mb: 2}}>
        <Grid
          item
          xs={12}
          sm="auto"
          sx={{
            display: 'flex',
            justifyContent: {xs: 'flex-end', sm: 'flex-start'},
            order: {xs: 1, sm: 2},
            mb: {xs: 1, sm: 0},
          }}>
          <Button size="small" onClick={() => setMoreInfoOpen(true)}>
            {t('library.moreInfo')}
          </Button>
          <Button
            size="small"
            sx={{ml: 1}}
            onClick={() => {
              const speedUnit = preferredUnits === 'metric' ? 'kph' : 'mph';
              const visibleColumns = columns.filter(
                c => c.field !== 'id' && c.field !== 'actions' && !c.hide,
              );
              const header = visibleColumns
                .map(c => {
                  const name = c.headerName ?? c.field;
                  if (c.field === 'speedAvgAvg') return `${name} ${speedUnit}`;
                  return name;
                })
                .join(',');
              const sortedRows = [...rows].sort(
                (a, b) => (b.count ?? 0) - (a.count ?? 0),
              );
              const csvRows = sortedRows.map(row =>
                visibleColumns
                  .map(col => {
                    const raw = row[col.field as keyof typeof row];
                    const formatted = formatCsvValue(col.field, raw);
                    const str = String(formatted ?? '');
                    return str.includes(',') ? `"${str}"` : str;
                  })
                  .join(','),
              );
              const csv = [header, ...csvRows].join('\n');
              const blob = new Blob([csv], {type: 'text/csv'});
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `HELIOS-${sanitizeFilename(teamName)}-drills-${selectedGroup}-${DateTime.local().toFormat('yyyy-MM-dd-HH-mm')}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}>
            {t('general.export')}
          </Button>
        </Grid>
        <Grid
          item
          xs={12}
          sm="auto"
          sx={{flexGrow: {sm: 1}, order: {xs: 2, sm: 1}}}>
          <Tabs
            value={selectedGroup}
            onChange={(_e, val) => setSelectedGroup(val)}>
            {Object.values(PositionGroup).map(group => (
              <Tab key={group} label={group} value={group} />
            ))}
          </Tabs>
        </Grid>
      </Grid>
      <Modal
        open={moreInfoOpen}
        onClose={() => setMoreInfoOpen(false)}
        aria-labelledby="drill-column-info-title">
        <Box sx={styles.modal}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}>
            <Typography variant="h5" id="drill-column-info-title">
              {t('library.columnDescriptions')}
            </Typography>
            <Button size="small" onClick={() => setMoreInfoOpen(false)}>
              {t('general.close')}
            </Button>
          </Box>
          {columnDescriptions.map(({label, desc}) => (
            <Box key={label} sx={{mb: 1.5}}>
              <Typography variant="subtitle2">{label}</Typography>
              <Typography variant="body2" color="text.secondary">
                {desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Modal>
      <Paper sx={{height: 650}}>
        <DataGrid
          rows={rows}
          columns={columns}
          onRowClick={handleRowClick}
          initialState={{
            sorting: {
              sortModel: [{field: 'count', sort: 'desc'}],
            },
          }}
        />
      </Paper>
      <Box sx={{display: 'flex', justifyContent: 'center', gap: 2, mt: 1}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <CircleIcon sx={{fontSize: 12, color: 'success.main'}} />
          <Typography variant="caption">{t('library.lowLoad')}</Typography>
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <CircleIcon sx={{fontSize: 12, color: 'warning.main'}} />
          <Typography variant="caption">{t('library.mediumLoad')}</Typography>
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
          <CircleIcon sx={{fontSize: 12, color: 'error.main'}} />
          <Typography variant="caption">{t('library.highLoad')}</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Drills;
